"use client";

import { getExchangeRates, convertAmount } from "@/lib/exchangeRates";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/* ------------------------------------------------------------------ */
/*  Supported currencies                                               */
/* ------------------------------------------------------------------ */

export const SUPPORTED_CURRENCIES = [
  { code: "GHS", label: "₵", name: "Cedis" },
  { code: "USD", label: "$", name: "US Dollar" },
  { code: "EUR", label: "€", name: "Euro" },
  { code: "GBP", label: "£", name: "British Pound" },
  { code: "NGN", label: "₦", name: "Naira" },
] as const;

export type SupportedCurrencyCode = (typeof SUPPORTED_CURRENCIES)[number]["code"];

const STORAGE_KEY = "dwellfinder_preferred_currency";

/* ------------------------------------------------------------------ */
/*  Context shape                                                      */
/* ------------------------------------------------------------------ */

interface CurrencyContextValue {
  /** The user's preferred display currency code (e.g. "USD"). */
  preferredCurrency: SupportedCurrencyCode;

  /** Update the preferred display currency. */
  setPreferredCurrency: (code: SupportedCurrencyCode) => void;

  /** Current exchange-rate table (base = "USD"). `null` while loading. */
  rates: Record<string, number> | null;

  /** Whether rates are currently being fetched. */
  ratesLoading: boolean;

  /**
   * Convert a price from its original currency to the preferred currency.
   * Returns `null` when rates are unavailable or currencies match.
   * If the original currency matches the preferred one, returns `null`
   * (meaning "no conversion needed — show original").
   */
  convert: (
    amount: number | string,
    fromCurrency: string,
  ) => { amount: number; isConverted: boolean } | null;
}

const CurrencyContext = createContext<CurrencyContextValue>({
  preferredCurrency: "USD",
  setPreferredCurrency: () => {},
  rates: null,
  ratesLoading: true,
  convert: () => null,
});

export const useCurrency = () => useContext(CurrencyContext);

/* ------------------------------------------------------------------ */
/*  Provider                                                           */
/* ------------------------------------------------------------------ */

export function CurrencyProvider({ children }: { children: React.ReactNode }) {
  const [preferredCurrency, setPreferredCurrencyState] =
    useState<SupportedCurrencyCode>("USD");
  const [rates, setRates] = useState<Record<string, number> | null>(null);
  const [ratesLoading, setRatesLoading] = useState(true);

  // Hydrate from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (
        stored &&
        SUPPORTED_CURRENCIES.some((c) => c.code === stored)
      ) {
        setPreferredCurrencyState(stored as SupportedCurrencyCode);
      }
    } catch {
      /* SSR or unavailable */
    }
  }, []);

  // Fetch rates on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await getExchangeRates("USD");
        if (!cancelled) setRates(r);
      } catch (err) {
        console.error("[CurrencyProvider] Failed to load exchange rates:", err);
      } finally {
        if (!cancelled) setRatesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setPreferredCurrency = useCallback(
    (code: SupportedCurrencyCode) => {
      setPreferredCurrencyState(code);
      try {
        localStorage.setItem(STORAGE_KEY, code);
      } catch {
        /* not critical */
      }
    },
    [],
  );

  const convert = useCallback(
    (amount: number | string, fromCurrency: string) => {
      const numAmount =
        typeof amount === "string" ? parseFloat(amount) : amount;
      if (isNaN(numAmount)) return null;

      const from = fromCurrency.toUpperCase();
      const to = preferredCurrency.toUpperCase();

      // Same currency — no conversion needed
      if (from === to) return { amount: numAmount, isConverted: false };

      if (!rates) return null;

      const converted = convertAmount(numAmount, from, to, rates);
      if (converted === null) return null;

      return { amount: converted, isConverted: true };
    },
    [preferredCurrency, rates],
  );

  const value = useMemo<CurrencyContextValue>(
    () => ({
      preferredCurrency,
      setPreferredCurrency,
      rates,
      ratesLoading,
      convert,
    }),
    [preferredCurrency, setPreferredCurrency, rates, ratesLoading, convert],
  );

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}
